import { FormSelect } from "@/components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "@/components/Toast";
import { useGetBudgetByCommunityData, useGetCommunityFinancialInfo, useGetDistributionGroupGL, useGetDistributionRuleByCommunityYear, useGetGLDistributionByCommunityYear, useGetGlMasterData, useGLAssignmentsActualMutation, useGLAssignmentsPlanMutation } from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ACTUAL_BLOCK, ACTUAL_DISTRIBUTED, ACTUAL_GROUP_DISTRIBUTION_GL, ACTUAL_PROPERTY, ACTUAL_ZONE, CATEGORY, DISTRIBUTION_TIME, GL_ACC, GL_DESC, GL_MASTER, ID, LIST, PERIODIC_RULE_OPTIONS, PLAN_BLOCK, PLAN_GROUP_DISTRIBUTION_GL, PLAN_PROPERTY, PLAN_ZONE, REAL_TIME, REAL_TIME_RULE_OPTIONS, YEAR } from "./consts";
import { activeLevels, ACTUALS, actualTab, BLOCK_TO_PROPERTY, COMMUNITY_TO_ZONE, distributionLevels, FIXED_WEIGHTS, PLANNED, plannedTab, WEIGHING_FACTOR, WEIGHING_FACTORS, ZONE_TO_BLOCK } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { routes } from "@/routes/utils";

const useDistributionRulesByGL = () => {
  const {glId} = useParams();
  const location = useLocation();
  const fromTraceability = location?.pathname?.includes(routes.traceability);
  const memberGls = location.state?.members;
  const [activeStatusTab, setActiveStatusTab] = useState(fromTraceability ? ACTUALS : PLANNED);
  const [globalFilter, setGlobalFilter] = useState(fromTraceability ? memberGls ? memberGls : glId : "");
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const validateGroupGl = useRef<any>(null);
  const isPlanned = useMemo(() => activeStatusTab == PLANNED, [activeStatusTab]);
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: {
      [LIST]: [
        {
          [ID]: "",
          [GL_MASTER]: "",
          [GL_ACC]: "",
          [GL_DESC]: "",
          [CATEGORY]: "",
          [YEAR]: "",
          [DISTRIBUTION_TIME]: isPlanned ? "REAL_TIME" : null,
          [PLAN_GROUP_DISTRIBUTION_GL]: "",
          [ACTUAL_GROUP_DISTRIBUTION_GL]: "",
          [PLAN_ZONE]: "",
          [PLAN_BLOCK]: "",
          [PLAN_PROPERTY]: "",
          [ACTUAL_ZONE]: "",
          [ACTUAL_BLOCK]: "",
          [ACTUAL_PROPERTY]: "",
          [ACTUAL_DISTRIBUTED]: "",
        }
      ]
    },
  });

  const communityData = useSelector((state: RootState) => state.currentData.communityData)
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear)
  const glList = useWatch({control: form.control, name: LIST});

  const { data: budgetData, isLoading: isBudgetDataLoading } =
    useGetBudgetByCommunityData({
      communityId: communityData?.communityId as string,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });
    
  const isApproveBudget = useMemo(() => !!budgetData?.data?.find((data: any) => data?.status?.toLowerCase() == "approved" && data?.year == currentFinanceYear), [budgetData]);

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear),
    },
  });

  const { data: groupGLData, isLoading: isLoadingGroupGL } = useGetDistributionGroupGL({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId),
    },
  });

  const {data: financialInfo, isLoading: isLoadingFinancialInfo } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  })

  const {data: communityRules, isLoading: isLoadingRules, refetch: refetchRules } = useGetDistributionRuleByCommunityYear({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })

  const {data: glDistribution, isLoading: isLoadingGLDistribution, refetch: refetchDistribution } = useGetGLDistributionByCommunityYear({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })
  
  useEffect(() => {
    if (glData?.data && glDistribution?.data) {
      const list = glData?.data?.filter((data: any) => !data?.groupDistributionGl)?.filter((data: any) => isPlanned ? data?.accountCategory == "I" || data?.accountCategory == "E" : data?.accountCategory == "E")
        ?.map((data: any, index: number) => {
        const glDistData = glDistribution?.data?.find((val: any) => data?.glMasterId == val?.communityGlMasterId);

        return ({
          [ID]: index,
          [GL_MASTER]: data?.glMasterId?.toString() || "",
          [GL_ACC]: data?.[GL_ACC] || "",
          [GL_DESC]: data?.[GL_DESC] || "",
          [CATEGORY]: data?.[CATEGORY] || "",
          [YEAR]: currentFinanceYear,
          [ACTUAL_DISTRIBUTED]: glDistData?.[ACTUAL_DISTRIBUTED],
          [DISTRIBUTION_TIME]: isPlanned ? "REAL_TIME" : glDistData?.[DISTRIBUTION_TIME] ? glDistData?.[DISTRIBUTION_TIME]  : null,
          [PLAN_GROUP_DISTRIBUTION_GL]: glDistData?.[PLAN_GROUP_DISTRIBUTION_GL],
          [ACTUAL_GROUP_DISTRIBUTION_GL]: glDistData?.[ACTUAL_GROUP_DISTRIBUTION_GL],
          [PLAN_ZONE]: glDistData?.[PLAN_ZONE] || "",
          [PLAN_BLOCK]: glDistData?.[PLAN_BLOCK] || "",
          [PLAN_PROPERTY]: glDistData?.[PLAN_PROPERTY] || "",
          [ACTUAL_ZONE]: glDistData?.[ACTUAL_ZONE] || "",
          [ACTUAL_BLOCK]: glDistData?.[ACTUAL_BLOCK] || "",
          [ACTUAL_PROPERTY]: glDistData?.[ACTUAL_PROPERTY] || "",
        })
      })

      form.setValue(LIST, list);
    }
  }, [glData, isPlanned, glDistribution]);

  const ruleStatusTabs = useMemo(() => {
    const distributionMode = financialInfo?.data?.distributionMode;
    if (fromTraceability) {
      return actualTab;
    } else if (distributionMode == PLANNED){
      return plannedTab;
    } else if (distributionMode == ACTUALS) {
      return actualTab;
    } else {
      return [...plannedTab, ...actualTab];
    }
  }, [financialInfo, fromTraceability]);

  const activeThresholdIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == financialInfo?.data?.distributionLevel
  ), [financialInfo])

  // 1. Memoize options helper with useCallback to stabilize its reference
  const getRuleOptions = useCallback((level: string) => {
    if (!communityRules?.data) return [];

    const filterData = communityRules?.data
      ?.filter((rule: any) => {
        if (rule?.distributionMethod == FIXED_WEIGHTS) {
          return rule?.level == level && rule?.budgetType == activeStatusTab
        }
        if (rule?.distributionMethod == WEIGHING_FACTOR || rule?.distributionMethod == WEIGHING_FACTORS) {
          return rule?.budgetType == activeStatusTab
        } 
        return true
    })

      // Map to the select option shape only on valid items
      if (filterData?.length) {
        return filterData?.map((data: any) => ({
          label: data?.ruleName || distributionLevels[data?.distributionMethod], // Consider humanizing this text if needed
          value: data?.distributionRuleId?.toString()
        }));
      } 
      return [];
  }, [communityRules?.data, activeStatusTab]);

  const timeOption = useMemo(() => {
    return isPlanned 
      ? REAL_TIME_RULE_OPTIONS 
      : [...REAL_TIME_RULE_OPTIONS, ...PERIODIC_RULE_OPTIONS];
  }, [isPlanned]);

  const groupGLOptions = useMemo(() => {
    const arr = (groupGLData?.data || [])?.map((data: any) => ({
      label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
      value: data?.glMasterId?.toString()
    }));

    return arr.length ? [{label: 'None', value: "none"}, ...arr] : arr;
  }, [groupGLData]);

  const columns = useMemo(() => {
    const disabled = isPlanned && isApproveBudget
    return ([
      {
        accessorKey: GL_ACC,
        header: "GL Account",
        size: 90,
      },
      {
        accessorKey: GL_DESC,
        header: "Description",
        size: 150,
      },
      {
        accessorKey: 'accountCategory',
        header: "Category",
        size: 150,
        cell: ({row}: any) => row?.original?.accountCategory == "I" ? "Income" : "Expense"
      },
      {
        accessorKey: "levelOneRule",
        header: "Level 1: Community To Zone",
        size: 200,
        cell: ({ row }: any) => {
          return (
            <FormSelect
              name={`${LIST}.${row.original.id}.${isPlanned ? PLAN_ZONE : ACTUAL_ZONE}`}
              control={form.control}
              label="Allocation Rule"
              options={getRuleOptions(COMMUNITY_TO_ZONE) || []}
              showSearch={false}
              placeholder="Please select"
              disabled={activeThresholdIndex < 0 || disabled || (!isPlanned && row?.original?.[ACTUAL_DISTRIBUTED]) || fromTraceability}
            />
          )
        },
      },
      {
        accessorKey: "levelTwoRule",
        header: "Level 2: Zone To Block",
        size: 180,
        cell: ({ row }: any) => (
          <FormSelect
            name={`${LIST}.${row.original.id}.${isPlanned ? PLAN_BLOCK : ACTUAL_BLOCK}`}
            control={form.control}
            label="Allocation Rule"
            options={getRuleOptions(ZONE_TO_BLOCK) || []}
            showSearch={false}
            placeholder="Please select"
            disabled={activeThresholdIndex < 1 || disabled || (!isPlanned && row?.original?.[ACTUAL_DISTRIBUTED]) || fromTraceability}
          />
        ),
      },
      {
        accessorKey: "levelThreeRule",
        header: "Level 3: Block To Property",
        size: 180,
        cell: ({ row }: any) => (
          <FormSelect
            name={`${LIST}.${row.original.id}.${isPlanned ? PLAN_PROPERTY : ACTUAL_PROPERTY}`}
            control={form.control}
            label="Allocation Rule"
            options={getRuleOptions(BLOCK_TO_PROPERTY) || []}
            showSearch={false}
            placeholder="Please select"
            disabled={activeThresholdIndex < 2 || disabled || (!isPlanned && row?.original?.[ACTUAL_DISTRIBUTED]) || fromTraceability}
          />
        ),
      },
      {
        accessorKey: "realTimeRule",
        header: "Real Time vs Periodic",
        size: 160,
        cell: ({ row }: any) => (
          <FormSelect
            name={`${LIST}.${row.original.id}.${DISTRIBUTION_TIME}`}
            control={form.control}
            label="Real Time vs Periodic"
            options={timeOption}
            showSearch={false}
            placeholder="Please select"
            disabled={isPlanned || disabled || (!isPlanned && row?.original?.[ACTUAL_DISTRIBUTED]) || fromTraceability}
            onChange={(e: any) => {
              if (!isPlanned && e == REAL_TIME) {
                form.setValue(`${LIST}.${row.original.id}.${ACTUAL_GROUP_DISTRIBUTION_GL}`, "")
              }
            }}
          />
        ),
      },
      {
        accessorKey: "groupGl",
        header: "Group GL",
        size: 120,
        cell: ({ row }: any) => {
          return (
            <FormSelect
              name={`${LIST}.${row.original.id}.${isPlanned ? PLAN_GROUP_DISTRIBUTION_GL : ACTUAL_GROUP_DISTRIBUTION_GL}`}
              control={form.control}
              label="Group GL"
              options={groupGLOptions}
              showSearch={false}
              placeholder="Please select"
              disabled={!isPlanned && form.watch(`${LIST}.${row.original.id}.${DISTRIBUTION_TIME}`) != "PERIODIC" || disabled || (!isPlanned && row?.original?.[ACTUAL_DISTRIBUTED]) || fromTraceability}
            />
          )
        },
      },
    ])
}, [activeThresholdIndex, getRuleOptions, groupGLOptions, isPlanned, isApproveBudget, glList]);

  const onStatusTabChange = (value: any) => {
    setActiveStatusTab(value)
  }

  const { mutate: glAssignPlan, isPending: isAssignPlanPending } =
    useGLAssignmentsPlanMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setShowWarningDialog(false);
        validateGroupGl.current = null;
        queryClient.invalidateQueries({queryKey: ["gl-distribution-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`GL Allocation Assigned Successfully`);
        
      },
    });

  const { mutate: glAssignActual, isPending: isAssignActualPending } =
    useGLAssignmentsActualMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setShowWarningDialog(false);
        validateGroupGl.current = null;
        queryClient.invalidateQueries({queryKey: ["gl-distribution-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`GL Allocation Assigned Successfully`);
        
      },
    });

    const getFilteredPayload = () => {
      let arr = [...glList];
    
      // 1. First Pass: Filter out empty rows based on active mode
      if (isPlanned) {
        arr = arr.filter((data: any) => data?.[PLAN_ZONE] || data?.[PLAN_BLOCK] || data?.[PLAN_PROPERTY])?.map((data: any) => ({...data, [PLAN_GROUP_DISTRIBUTION_GL]: data?.[PLAN_GROUP_DISTRIBUTION_GL] == "none" ? "" : data?.[PLAN_GROUP_DISTRIBUTION_GL]}));
      } else {
        // FIX: Typo corrected here (you had ACTUAL_PROPERTY listed twice instead of ACTUAL_BLOCK)
        arr = arr.filter((data: any) => data?.[ACTUAL_ZONE] || data?.[ACTUAL_BLOCK] || data?.[ACTUAL_PROPERTY])?.map((data: any) => ({...data, [ACTUAL_GROUP_DISTRIBUTION_GL]: data?.[ACTUAL_GROUP_DISTRIBUTION_GL] == "none" ? "" : data?.[ACTUAL_GROUP_DISTRIBUTION_GL]}));
      }
    
      // 2. Second Pass: Filter out matching existing records SEPARATELY by rule targets
      arr = arr.filter((data: any) => {
        const isExistingMatch = glDistribution?.data?.some((val: any) => {
          // The baseline GL Master must always match
          const isSameGl = data?.communityGlMasterId === val?.communityGlMasterId;
          if (!isSameGl) return false;
    
          if (isPlanned) {
            // For Planned mode, consider it "existing" only if the target rules line up
            return (
              data?.[PLAN_ZONE] === val?.[PLAN_ZONE] &&
              data?.[PLAN_BLOCK] === val?.[PLAN_BLOCK] &&
              data?.[PLAN_PROPERTY] === val?.[PLAN_PROPERTY]
            );
          } else {
            // For Actuals mode, verify against actual rule targets
            return (
              data?.[ACTUAL_ZONE] === val?.[ACTUAL_ZONE] &&
              data?.[ACTUAL_BLOCK] === val?.[ACTUAL_BLOCK] &&
              data?.[ACTUAL_PROPERTY] === val?.[ACTUAL_PROPERTY]
            );
          }
        });
    
        // If a matching configuration exists, return false to filter it out
        return !isExistingMatch;
      });
    
      return arr;
    };

    const onSave = () => {
      const arr = getFilteredPayload();

      if (arr?.length) {
        let validate: any[] = [];
  
        validate = arr?.filter((data: any) => data?.[PLAN_GROUP_DISTRIBUTION_GL] && data?.[ACTUAL_GROUP_DISTRIBUTION_GL])
        ?.map((data: any) => {
          if (data[PLAN_GROUP_DISTRIBUTION_GL] != data[ACTUAL_GROUP_DISTRIBUTION_GL]) {
            return {isDifferentGl: true, [GL_ACC]: data?.[GL_ACC]}
          }
        })
  
        const result = arr?.reduce(
          (acc: any, data: any) => {
            const planGl = data?.[PLAN_GROUP_DISTRIBUTION_GL];
            const actualGl = data?.[ACTUAL_GROUP_DISTRIBUTION_GL];
        
            // 1. Group by Plan GL Account Number
            if (planGl) {
              if (!acc.planGroups[planGl]) {
                acc.planGroups[planGl] = [];
              }
              acc.planGroups[planGl].push(data);
            }
        
            // 2. Group by Actual GL Account Number
            if (actualGl) {
              if (!acc.actualGroups[actualGl]) {
                acc.actualGroups[actualGl] = [];
              }
              acc.actualGroups[actualGl].push(data);
            }
        
            return acc;
          },
          { planGroups: {}, actualGroups: {} }
        ) || { planGroups: {}, actualGroups: {} };
        
        const actualGrpKeys = Object.keys(result.actualGroups);
        Object.keys(result.planGroups)?.map((key) => {
          if (actualGrpKeys.includes(key) && result.actualGroups[key].length && result.planGroups[key].length != result.actualGroups[key].length) {
            validate.push({isDiffCount: true, [GL_ACC]: glData?.data?.find((val: any) => result.planGroups[key]?.[0]?.[PLAN_GROUP_DISTRIBUTION_GL] == val?.glMasterId)?.glAccountNumber, planCount: result.planGroups[key].length, actualCount: result.actualGroups[key].length})
          }
        })
        
        if (validate?.filter((val: any) => Boolean(val))?.length) {
          validateGroupGl.current = validate;
          setShowWarningDialog(true);
        } else {
          onConfirm();
        }
      } else {
        toast.warning("No GL(s) found for update");
      }
    };

    const onConfirm = () => {
      if (isPlanned) {
        glAssignPlan({
          communityId: communityData?.communityId,
          data: [...getFilteredPayload()]
        })
      } else {
        glAssignActual({
          communityId: communityData?.communityId,
          data: [...getFilteredPayload()]
        })
      }
    }

    useEffect(() => {
      refetchRules();
      refetchDistribution();
    }, [activeStatusTab]);

  const isLoading = isLoadingGlMaster || isLoadingFinancialInfo || isLoadingRules || isLoadingGLDistribution || isLoadingGroupGL || isBudgetDataLoading;

  return {
    activeStatusTab,
    columns,
    ruleStatusTabs,
    onSave,
    isLoadingRules,
    glList,
    onStatusTabChange,
    isAssignPending: isAssignPlanPending || isAssignActualPending,
    isLoading,
    globalFilter,
    setGlobalFilter,
    showWarningDialog,
    setShowWarningDialog,
    validateGroupGl: validateGroupGl.current?.filter((data: any) => data?.isDifferentGl) || [],
    validateGroupGlCount: validateGroupGl.current?.filter((data: any) => data?.isDiffCount) || [],
    onConfirm,
    isLockPlan: isApproveBudget && isPlanned,
    fromTraceability
  };
};

export default useDistributionRulesByGL;
